import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { RemoteDiagnosticsCloudApiPicker, UseCases, UseCaseVddList } from '@vi-iot/client';

import { RemoteDiagnosticsControlUnit, RemoteDiagnosticsStore } from '../remote-diagnostics';
import { RemoteDiagnosticsActions } from '../remote-diagnostics/remote-diagnostics.actions';

import { RemoteDiagnosticsUseCasesActions } from './remote-diagnostics-use-cases.actions';
import { serviceLayerVddGroups } from './remote-diagnostics-use-cases.model';
import { RemoteDiagnosticsUseCasesStore } from './remote-diagnostics-use-cases.store';

@Injectable()
export class RemoteDiagnosticsUseCasesEffects {
  loadUseCases$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsUseCasesActions.loadUseCasesAttempt),
      concatLatestFrom((action) => [
        this.remoteDiagnosticsStore.getDiagStackConnectionProperties(action.gatewaySerial),
        this.remoteDiagnosticsStore.getMainLogicalLinkFromTopology(action.gatewaySerial),
        this.remoteDiagnosticsStore.getSelectedVinNumber(action.gatewaySerial),
      ]),
      switchMap(([_action, diagStackConnectionProperties, mainLogicalLink, vinNumber]) =>
        this.remoteDiagnosticsCloudApiPicker
          .instance()
          .diagnosticInfoUseCases(
            diagStackConnectionProperties.node,
            diagStackConnectionProperties.xAuthToken,
            mainLogicalLink,
            diagStackConnectionProperties.vciName,
            diagStackConnectionProperties.vciResource,
            vinNumber
          )
          .pipe(
            map((useCases: UseCases) =>
              RemoteDiagnosticsUseCasesActions.loadUseCasesSuccess({
                useCases,
              })
            ),
            catchError((error) =>
              of(RemoteDiagnosticsUseCasesActions.loadUseCasesFailed({ error }))
            )
          )
      )
    );
  });

  loadUseCaseVddList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RemoteDiagnosticsUseCasesActions.loadUseCaseVddListAttempt),
      concatLatestFrom((action) => [
        this.remoteDiagnosticsStore.getDiagStackConnectionProperties(action.gatewaySerial),
        this.remoteDiagnosticsStore.getMainLogicalLinkFromTopology(action.gatewaySerial),
        this.remoteDiagnosticsStore.getSelectedVinNumber(action.gatewaySerial),
        this.remoteDiagnosticsUseCasesStore.getSelectedUseCaseType(action.gatewaySerial),
      ]),
      switchMap(
        ([
          action,
          diagStackConnectionProperties,
          mainLogicalLink,
          vinNumber,
          selectedUseCaseType,
        ]) =>
          this.remoteDiagnosticsCloudApiPicker
            .instance()
            .diagnosticInfoUseCaseVddList(
              diagStackConnectionProperties.node,
              diagStackConnectionProperties.xAuthToken,
              mainLogicalLink,
              diagStackConnectionProperties.vciName,
              diagStackConnectionProperties.vciResource,
              vinNumber,
              action.useCase,
              selectedUseCaseType
            )
            .pipe(
              map((vddList: UseCaseVddList) =>
                RemoteDiagnosticsUseCasesActions.loadUseCaseVddListSuccess({
                  gatewaySerial: action.gatewaySerial,
                  vddList: vddList.structure,
                })
              ),
              catchError((error) =>
                of(RemoteDiagnosticsUseCasesActions.loadUseCaseVddListFailed({ error }))
              )
            )
      )
    );
  });

  selectUseCaseVddList$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          RemoteDiagnosticsUseCasesActions.loadUseCaseVddListSuccess,
          RemoteDiagnosticsUseCasesActions.selectUseCaseVddList
        ),
        concatLatestFrom((action) => [
          this.remoteDiagnosticsUseCasesStore.getSelectedUseCaseGroup(action.gatewaySerial),
          this.remoteDiagnosticsStore.getMainLogicalLinkFromTopology(action.gatewaySerial),
          this.remoteDiagnosticsStore.getControlUnits(action.gatewaySerial),
        ]),
        map(([action, selectedUseCaseGroup, mainLogicalLinkFromTopology, controlUnits]) => {
          if (selectedUseCaseGroup) {
            this.store.dispatch(
              RemoteDiagnosticsActions.deleteUseCaseParameters({
                gatewaySerial: action.gatewaySerial,
              })
            );
            const mainLogicalLink: RemoteDiagnosticsControlUnit = controlUnits.find(
              (controlUnit) => controlUnit.value === mainLogicalLinkFromTopology
            );

            const serviceLayerVdds = serviceLayerVddGroups.find(
              (vddList) => vddList.usecaseName === selectedUseCaseGroup
            )?.structure;
            const useCaseVdds = action.vddList || serviceLayerVdds;

            useCaseVdds.forEach((useCaseVdd) => {
              const vdd = {
                name: useCaseVdd.vdd,
                value: this.translateService.instant('TI_' + useCaseVdd.vdd),
                vddInterval: null,
                isWrite: true,
                isUseCaseVdd: true,
                isRead: true,
              };
              this.store.dispatch(
                RemoteDiagnosticsActions.parameter.add({
                  controlUnit: mainLogicalLink,
                  vdd,
                  gatewaySerial: action.gatewaySerial,
                })
              );
            });
          }
        })
      );
    },
    { dispatch: false }
  );

  constructor(
    private remoteDiagnosticsCloudApiPicker: RemoteDiagnosticsCloudApiPicker,
    private actions$: Actions,
    private store: Store,
    private remoteDiagnosticsStore: RemoteDiagnosticsStore,
    private remoteDiagnosticsUseCasesStore: RemoteDiagnosticsUseCasesStore,
    private translateService: TranslateService
  ) {}
}
